import { useEffect, useRef, useState } from "react";
import { useHoldToRecord } from "../../hooks/useHoldToRecord";
import { useRealtimeTranscription } from "../../hooks/useRealtimeTranscription";
import { transcribeSurveyAudio, accessHeaders } from "../../lib/survey-api";

/**
 * Tap-to-talk voice input for the survey.
 *
 * Primary path is LIVE streaming: words appear in the answer box in real time as
 * the person speaks (ElevenLabs Scribe realtime over a browser WebSocket, minted
 * via /api/survey/realtime-token). If streaming is unavailable (budget cap, an
 * unsupported browser, a socket error), it falls back to the batch path for the
 * rest of the session: record, tap stop, transcribe, drop into the box.
 *
 * Owns nothing about the text itself: it reads `value` (the current draft) to
 * snapshot a base when recording starts, then writes the growing text back via
 * `onChange`. So dictation appends to whatever the person already typed.
 */
export function MicButton({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const [err, setErr] = useState<string | null>(null);
  const [transcribing, setTranscribing] = useState(false);
  // Once realtime fails, stay on the batch path for the rest of the session.
  const [mode, setMode] = useState<"realtime" | "batch">("realtime");

  const baseRef = useRef(""); // draft snapshot taken when recording starts
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const compose = (snippet: string) => {
    const base = baseRef.current.trim();
    const s = snippet.trim();
    if (!s) return base;
    return base ? `${base} ${s}` : s;
  };

  // --- Batch fallback (record → stop → transcribe) ---
  const batch = useHoldToRecord({
    onSubmit: async (blob) => {
      setTranscribing(true);
      setErr(null);
      const r = await transcribeSurveyAudio(blob);
      setTranscribing(false);
      batchRef.current.reset();
      if (r.ok && r.text && r.text.trim()) onChangeRef.current(compose(r.text.trim()));
      else setErr("Didn't catch that. Try again.");
    },
    onError: (reason) => {
      batchRef.current.reset();
      if (reason === "too_short") setErr(null);
      else if (reason === "permission_denied") setErr("Mic access is blocked. Allow it in your browser.");
      else if (reason === "no_media_recorder") setErr("This browser can't record audio.");
      else setErr("Recording failed. Try again.");
    },
  });
  const batchRef = useRef(batch);
  batchRef.current = batch;

  const startBatch = () => {
    setMode("batch");
    batchRef.current.reset();
    requestAnimationFrame(() => batchRef.current.start());
  };

  // --- Realtime (primary) ---
  const rt = useRealtimeTranscription({
    tokenEndpoint: "/api/survey/realtime-token",
    tokenHeaders: accessHeaders(),
    onFinal: ({ text }) => {
      if (text.trim()) onChangeRef.current(compose(text));
    },
    onError: (reason) => {
      if (reason === "permission_denied") {
        setErr("Mic access is blocked. Allow it in your browser.");
        setMode("batch"); // next tap uses batch (also needs permission)
        return;
      }
      // token / socket / quota issue: fall back to batch recording for this tap.
      startBatch();
    },
  });
  const rtRef = useRef(rt);
  rtRef.current = rt;

  // Stream interim words into the box live while listening.
  useEffect(() => {
    if (rt.status === "listening" && rt.interim) onChangeRef.current(compose(rt.interim));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rt.interim, rt.status]);

  const isRecording = rt.status === "listening" || batch.state === "recording";
  const isPreparing = rt.status === "connecting" || batch.state === "preparing";
  const isBusy = transcribing || rt.status === "finalizing" || batch.state === "stopping";

  const toggle = () => {
    const r = rtRef.current;
    const b = batchRef.current;
    // Stop / cancel whatever is currently active.
    if (r.status === "listening") {
      void r.stop();
      return;
    }
    if (r.status === "connecting") {
      r.reset();
      return;
    }
    if (b.state === "recording") {
      b.stop();
      return;
    }
    if (b.state === "preparing") {
      b.cancel();
      return;
    }
    // Start fresh: snapshot the current draft, then begin.
    setErr(null);
    baseRef.current = valueRef.current;
    if (mode === "batch") {
      startBatch();
      return;
    }
    r.reset();
    // Defer so the hook's idle guard passes after reset re-renders.
    requestAnimationFrame(() => void rtRef.current.start());
  };

  const label = isRecording
    ? "Listening… Tap to stop"
    : isBusy
      ? "Writing it down…"
      : isPreparing
        ? "Allow your mic…"
        : "Tap to speak";

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative">
        {isRecording && (
          <span className="absolute inset-0 rounded-full ring-2 ring-accent-600/50 radiate pointer-events-none" aria-hidden="true" />
        )}
        <button
          type="button"
          onClick={toggle}
          disabled={disabled || isBusy}
          className={[
            "flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-200 border",
            isRecording
              ? "bg-accent-600 text-parchment-50 border-accent-600"
              : "bg-parchment-50 text-ink-700 border-parchment-300 hover:border-ink-300",
            isPreparing ? "border-accent-600/40 text-ink-900" : "",
            disabled || isBusy ? "opacity-50 cursor-not-allowed" : "",
          ].join(" ")}
          aria-pressed={isRecording}
          aria-label={isRecording ? "Stop recording" : isPreparing ? "Cancel microphone" : "Speak your answer"}
        >
          {isRecording ? <StopIcon /> : <MicIcon />}
          <span>{label}</span>
        </button>
      </div>
      {err && <p className="text-xs text-accent-600 shake">{err}</p>}
    </div>
  );
}

function MicIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <path d="M12 17v5" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="5" y="5" width="14" height="14" rx="2" />
    </svg>
  );
}
